import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ArrowRight, CheckCircle2, Clock, CreditCard, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function PendingPaymentsPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/orders");
      // Only show pending orders that a real customer created (skip
      // "included with booking" admin-side placeholders).
      const pending = (r.data || []).filter(
        (o) => o.status === "pending" && !o.included_with_booking,
      );
      setOrders(pending);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste bestillinger");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.trim().toLowerCase();
    return orders.filter((o) =>
      [o.customer_name, o.customer_email, o.booking_id, (o.items || []).map((i) => i.name_no).join(" ")]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [orders, search]);

  const markPaid = async (id) => {
    if (!window.confirm("Marker denne bestillingen som betalt (kontant)?")) return;
    setBusyId(id);
    try {
      await api.patch(`/orders/${id}/mark-paid`, { payment_method: "cash" });
      toast.success("Markert som betalt");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feil");
    } finally {
      setBusyId("");
    }
  };

  const cancel = async (o) => {
    const msg = o.stripe_session_id
      ? "Kansellere denne bestillingen? Den åpne Stripe-siden vil utløpe."
      : "Kansellere denne bestillingen?";
    if (!window.confirm(msg)) return;
    setBusyId(o.id);
    try {
      const r = await api.patch(`/orders/${o.id}/cancel`);
      if (r.data?.stripe_session_expired) {
        toast.success("Kansellert · Stripe-sesjonen utløpt");
      } else if (o.stripe_session_id) {
        toast.warning("Kansellert lokalt, men Stripe-sesjonen kunne ikke utløpes. Sjekk i Stripe Dashboard.");
      } else {
        toast.success("Kansellert");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feil");
    } finally {
      setBusyId("");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("nb-NO", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const total = orders.reduce((s, o) => s + (o.subtotal_eur || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="pending-payments-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39] font-semibold">
            Ubetalte bestillinger
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-0.5">
            Ventende betalinger
          </h1>
          <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
            Kunde-bestillinger som venter på betaling. Marker som betalt hvis
            kunden har betalt kontant/Vipps, eller kanseller for å utløpe
            Stripe-sesjonen så kunden ikke kan fullføre etterpå.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#A57F39]">
            Åpent beløp
          </div>
          <div className="text-2xl font-mono font-bold text-[#0C1824]">
            {total.toFixed(0)} €
          </div>
          <div className="text-[11px] text-slate-500">
            {orders.length} bestilling{orders.length === 1 ? "" : "er"}
          </div>
        </div>
      </div>

      <div className="mb-5 max-w-md">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søk kunde, e-post, produkt…"
          data-testid="pending-payments-search"
        />
      </div>

      {loading ? (
        <Card className="p-10 flex items-center justify-center text-[#A57F39]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Laster ventende betalinger…
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <div className="font-semibold text-[#0C1824]">Alt er oppgjort.</div>
          <div className="text-sm text-[#2B3F4D] mt-1">
            Ingen ventende kunde-betalinger akkurat nå.
          </div>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="pending-payments-list">
          {filtered.map((o) => (
            <Card
              key={o.id}
              data-testid={`pending-order-${o.id}`}
              className="p-4 md:p-5"
            >
              <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                      <Clock className="w-3 h-3 mr-1" /> Venter
                    </Badge>
                    {o.stripe_session_id && (
                      <Badge className="bg-[#635BFF]/10 text-[#635BFF] border border-[#635BFF]/25">
                        <CreditCard className="w-3 h-3 mr-1" /> Stripe åpen
                      </Badge>
                    )}
                    <span className="text-xs text-slate-500">
                      Opprettet {fmtDate(o.created_at)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <div className="text-lg font-semibold text-[#0C1824] truncate">
                      {o.customer_name || "Ukjent kunde"}
                    </div>
                    {o.customer_email && (
                      <span className="text-xs text-slate-500">· {o.customer_email}</span>
                    )}
                  </div>
                  <ul className="mt-2 space-y-0.5">
                    {(o.items || []).map((it, i) => (
                      <li
                        key={i}
                        className="text-sm text-[#2B3F4D] flex justify-between max-w-md"
                      >
                        <span>× {it.quantity} {it.name_no}</span>
                        <span className="font-mono text-[#A57F39]">{it.line_total_eur} €</span>
                      </li>
                    ))}
                    <li className="text-sm font-semibold text-[#0C1824] flex justify-between max-w-md pt-1.5 border-t border-slate-100 mt-1.5">
                      <span>Sum</span>
                      <span className="font-mono">{o.subtotal_eur} €</span>
                    </li>
                  </ul>
                  {o.booking_id && (
                    <Link
                      to={`/admin/bookings/${o.booking_id}`}
                      data-testid={`pending-order-goto-${o.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#1DA3B3] hover:underline mt-2"
                    >
                      Åpne booking <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                <div className="flex flex-row md:flex-col gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => markPaid(o.id)}
                    disabled={busyId === o.id}
                    data-testid={`pending-order-mark-paid-${o.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                    Marker betalt
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cancel(o)}
                    disabled={busyId === o.id}
                    data-testid={`pending-order-cancel-${o.id}`}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" />
                    Kanseller
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
